import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import { fetchDistributorVenueDetail } from '../../../../services/Http/DistributorApi';
import { useCurrency } from '../../../../context/CurrencyProvider';

const DistributorVenueDetail = () => {
    const { venueId } = useParams();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadDetail = async () => {
            try {
                const response = await fetchDistributorVenueDetail(venueId);
                setData(response);
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadDetail();
    }, [venueId]);

    const stats = useMemo(() => data?.metrics || {}, [data]);

    if (loading) {
        return <LoadingMessage>{t('DistributorVenueDetail.loading')}</LoadingMessage>;
    }

    if (error || !data) {
        return <ErrorMessage>{t('DistributorVenueDetail.error')}</ErrorMessage>;
    }

    const { venue, top_items: topItems = [] } = data;

    return (
        <PageContainer>
            <PageHeader>
                <div>
                    <PageTitle>{venue.name}</PageTitle>
                    <Subtitle>{t('DistributorVenueDetail.subtitle', { city: venue.city || '' })}</Subtitle>
                </div>
                <BackLink to="/venues">{t('DistributorVenueDetail.back')}</BackLink>
            </PageHeader>
            <PageContent>
                <StatsRow>
                    <StatCard>
                        <StatLabel>{t('DistributorVenueDetail.stats.outbound')}</StatLabel>
                        <StatValue>{formatResolvedAmount({
                            eurAmount: stats.outbound_value,
                            localAmount: stats.outbound_value_local ?? stats.outbound_local,
                            localCurrencyId: stats.seller_currency_id ?? stats.currency_id,
                            localCurrencyCode: stats.seller_currency_code ?? stats.currency_code,
                        })} / {stats.outbound_quantity || 0} {t('DistributorVenueDetail.stats.units')}</StatValue>
                    </StatCard>
                    <StatCard>
                        <StatLabel>{t('DistributorVenues.table.columns.lastActivity')}</StatLabel>
                        <StatValue>{formatDate(stats.last_transaction_at)}</StatValue>
                    </StatCard>
                </StatsRow>

                <Panel>
                    <PanelTitle>{t('DistributorVenueDetail.topItems.title')}</PanelTitle>
                    {topItems.length === 0 && <MutedText>{t('DistributorVenueDetail.topItems.empty')}</MutedText>}
                    {topItems.length > 0 && (
                        <List>
                            {topItems.map((item) => (
                                <li key={item.id}>
                                    <div>
                                        <strong>{item.name}</strong>
                                        <small>{t('DistributorVenueDetail.topItems.metrics', {
                                            quantity: item.quantity,
                                            value: formatResolvedAmount({
                                                eurAmount: item.value,
                                                localAmount: item.value_local,
                                                localCurrencyId: item.seller_currency_id ?? item.currency_id,
                                                localCurrencyCode: item.seller_currency_code ?? item.currency_code,
                                            }),
                                        })}</small>
                                    </div>
                                </li>
                            ))}
                        </List>
                    )}
                </Panel>
            </PageContent>
        </PageContainer>
    );
};

export default DistributorVenueDetail;

const formatDate = (value) => {
    if (!value) {
        return '—';
    }
    return new Date(value).toLocaleString();
};

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

const BackLink = styled(Link)`
    color: #821692;
    font-weight: 600;
`;

const StatsRow = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 1rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
`;

const StatLabel = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;

const StatValue = styled.p`
    margin: 0.25rem 0 0;
    font-size: 1.3rem;
    font-weight: 600;
`;

const Panel = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 1.25rem;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
`;

const PanelTitle = styled.h4`
    margin-top: 0;
`;

const List = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        padding: 0.625rem 0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    li:last-child {
        border-bottom: none;
    }

    small {
        color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
    }
`;

const MutedText = styled.p`
    color: ${({ theme }) => theme.colors?.textSecondary || '#666'};
`;
